import { types, getSnapshot, applySnapshot, destroy, onSnapshot } from 'mobx-state-tree';
import { v4 as uuidv4 } from 'uuid';
import { jsPDF } from 'jspdf';
import { Page } from './page-model';
import { History } from './history';
import { TYPES_MAP, forEveryChild } from './group-model';

// Detect embedded base64 data URLs (data:...;base64,...).
// Regular http/https/blob URLs are preserved.
const isBase64DataUrl = (v) =>
  typeof v === 'string' && v.startsWith('data:') && v.includes(';base64,');

// Only strip base64 from variable input-field images (inputField: true).
// Static design images ARE the template content — stripping them clears the design.
// Variable input-field placeholders are replaced at runtime anyway, so stripping
// their (usually tiny SVG) placeholder base64 is safe and keeps exports clean.
const stripBase64FromElement = (el) => {
  const out = { ...el };
  const isInputField = out.custom && out.custom.inputField;
  if (isInputField) {
    if (isBase64DataUrl(out.src)) out.src = '';
    if (isBase64DataUrl(out.clipSrc)) out.clipSrc = '';
  }
  if (Array.isArray(out.children)) {
    out.children = out.children.map(stripBase64FromElement);
  }
  return out;
};

const stripBase64FromExport = (data) => ({
  ...data,
  pages: (data.pages || []).map((page) => ({
    ...page,
    children: (page.children || []).map(stripBase64FromElement),
  })),
});

const Font = types.model('Font', {
  fontFamily: types.string,
  url: types.optional(types.string, ''),
  styles: types.optional(types.frozen(), []),
});

const Audio = types
  .model('Audio', {
    id: types.optional(types.identifier, () => uuidv4()),
    src: types.optional(types.string, ''),
    duration: types.optional(types.number, 0),
    startTime: types.optional(types.number, 0),
    endTime: types.optional(types.number, 0),
    volume: types.optional(types.number, 1),
    delay: types.optional(types.number, 0),
    custom: types.optional(types.frozen(), {}),
  })
  .actions((self) => ({
    toJSON() {
      return {
        id: self.id,
        src: self.src,
        duration: self.duration,
        startTime: self.startTime,
        endTime: self.endTime,
        volume: self.volume,
        delay: self.delay,
        custom: self.custom,
      };
    },
    set(attrs) {
      for (const [key, value] of Object.entries(attrs)) {
        if (key in self && key !== 'id') {
          self[key] = value;
        }
      }
    },
  }));

export const Store = types
  .model('Store', {
    role: types.optional(types.string, 'design'),
    pages: types.optional(types.array(Page), []),
    fonts: types.optional(types.array(Font), []),
    audios: types.optional(types.array(Audio), []),
    width: types.optional(types.number, 500),
    height: types.optional(types.number, 500),
    scale: types.optional(types.number, 1),
    scaleToFit: types.optional(types.number, 1),
    unit: types.optional(types.string, 'px'),
    dpi: types.optional(types.number, 72),
    schemaVersion: types.optional(types.number, 1),
    bleedVisible: types.optional(types.boolean, false),
    rulesVisible: types.optional(types.boolean, false),
    distanceGuidesVisible: types.optional(types.boolean, false),
    openedSidePanel: types.optional(types.string, ''),
    previousOpenedSidePanel: types.optional(types.string, ''),
    custom: types.optional(types.frozen(), {}),
    tool: types.optional(types.string, 'selection'),
    selectedElementsIds: types.optional(types.array(types.string), []),
    history: types.optional(History, {}),
    _activePageId: types.optional(types.string, ''),
    _selectedPagesIds: types.optional(types.array(types.string), []),
    _elementsPixelRatio: types.optional(types.number, 1),
  })
  .volatile(() => ({
    _stageRef: null,
    _listeners: new Map(),
    _key: '',
    _showCredit: false,
    currentTime: 0,
    isPlaying: false,
    _toolbarPanel: null, // null | { type: 'effects'|'mask', elementId: string }
    _fontVersion: 0,    // incremented after each font load to trigger canvas re-render
    _pendingInputFields: [], // elements awaiting user input after loadJSON
  }))
  .views((self) => ({
    get _idsMap() {
      const map = {};
      self.pages.forEach((page) => {
        page.children.forEach((child) => {
          forEveryChild(child, (el) => {
            map[el.id] = el;
          });
        });
      });
      return map;
    },
    get selectedElements() {
      const map = self._idsMap;
      return self.selectedElementsIds
        .map((id) => map[id])
        .filter(Boolean);
    },
    get selectedShapes() {
      return self.selectedElements;
    },
    get children() {
      return self.pages;
    },
    get activePage() {
      if (self._activePageId) {
        const page = self.pages.find((p) => p.id === self._activePageId);
        if (page) return page;
      }
      return self.pages.length > 0 ? self.pages[0] : undefined;
    },
    get selectedPages() {
      if (self._selectedPagesIds.length > 0) {
        return self._selectedPagesIds
          .map((id) => self.pages.find((p) => p.id === id))
          .filter(Boolean);
      }
      const active = self.activePage;
      return active ? [active] : [];
    },
    get duration() {
      let total = 0;
      self.pages.forEach((page) => {
        total += page.duration;
      });
      return total;
    },
    get inputFields() {
      const fields = [];
      self.pages.forEach((page) => {
        page.children.forEach((el) => {
          forEveryChild(el, (child) => {
            if (child.custom && child.custom.inputField) {
              fields.push(child);
            }
          });
        });
      });
      return fields;
    },
    get _hasCroppedImages() {
      for (const page of self.pages) {
        for (const child of page.children) {
          if (
            child.type === 'image' &&
            (child.cropX !== 0 ||
              child.cropY !== 0 ||
              child.cropWidth !== 1 ||
              child.cropHeight !== 1)
          ) {
            return true;
          }
        }
      }
      return false;
    },
  }))
  .actions((self) => ({
    find(callback) {
      for (const page of self.pages) {
        if (callback(page)) return page;
        for (const child of page.children) {
          if (callback(child)) return child;
        }
      }
      return undefined;
    },
    getElementById(id) {
      return self._idsMap[id];
    },
  }))
  .actions((self) => ({
    requestInputFields() {
      const fields = self.inputFields;
      self._pendingInputFields = fields;
      return fields;
    },
    _resolveInputField(id) {
      self._pendingInputFields = self._pendingInputFields.filter(
        (el) => el.id !== id
      );
    },
    _unresolveInputField(el) {
      // Re-insert at the front so it becomes the next field shown (back navigation)
      const alreadyPending = self._pendingInputFields.some((f) => f.id === el.id);
      if (!alreadyPending) {
        self._pendingInputFields.unshift(el);
      }
    },
    clearPendingInputFields() {
      self._pendingInputFields = [];
    },
  }))
  .actions((self) => ({
    afterCreate() {
      // set initial active page
      if (self.pages.length > 0 && !self._activePageId) {
        self._activePageId = self.pages[0].id;
      }
    },
    set(attrs) {
      for (const [key, value] of Object.entries(attrs)) {
        if (key in self && !key.startsWith('_')) {
          self[key] = value;
        }
      }
    },
    setUnit({ unit, dpi }) {
      if (unit !== undefined) self.unit = unit;
      if (dpi !== undefined) self.dpi = dpi;
    },
    setRole(role) {
      self.role = role;
    },
    setTool(tool) {
      self.tool = tool;
    },
    setToolOptions(options) {
      // placeholder for tool options
    },
    addPage(attrs = {}) {
      const pageData = {
        id: uuidv4(),
        ...attrs,
      };
      self.pages.push(pageData);
      const newPage = self.pages[self.pages.length - 1];
      self._activePageId = newPage.id;
      return newPage;
    },
    selectPage(id) {
      self._activePageId = id;
      self._selectedPagesIds.clear();
    },
    selectPages(ids) {
      self._selectedPagesIds.replace(ids);
      if (ids.length > 0) {
        self._activePageId = ids[0];
      }
    },
    selectElements(ids) {
      self.selectedElementsIds.replace(ids);
      // Close toolbar panel if the selected element changes
      if (self._toolbarPanel) {
        const stillSelected = ids.includes(self._toolbarPanel.elementId);
        if (!stillSelected) {
          self._toolbarPanel = null;
        }
      }
    },
    toggleBleed(bleedVisible) {
      self.bleedVisible =
        bleedVisible !== undefined ? bleedVisible : !self.bleedVisible;
    },
    toggleRulers(rulesVisible) {
      self.rulesVisible =
        rulesVisible !== undefined ? rulesVisible : !self.rulesVisible;
    },
    toggleDistanceGuides(distanceGuidesVisible) {
      self.distanceGuidesVisible =
        distanceGuidesVisible !== undefined
          ? distanceGuidesVisible
          : !self.distanceGuidesVisible;
    },
    openSidePanel(panelName) {
      self.previousOpenedSidePanel = self.openedSidePanel;
      self.openedSidePanel = panelName;
    },
    openToolbarPanel(type) {
      const el = self.selectedElements.length === 1 ? self.selectedElements[0] : null;
      if (el) {
        self._toolbarPanel = { type, elementId: el.id };
      }
    },
    closeToolbarPanel() {
      self._toolbarPanel = null;
    },
    setScale(scale) {
      self.scale = scale;
    },
    _setScaleToFit(scale) {
      self.scaleToFit = scale;
    },
    setElementsPixelRatio(ratio) {
      self._elementsPixelRatio = ratio;
    },
    setSize(width, height, useMagic = false) {
      if (width !== undefined) self.width = width;
      if (height !== undefined) self.height = height;
    },
    setPageZIndex(id, zIndex) {
      const idx = self.pages.findIndex((p) => p.id === id);
      if (idx >= 0 && zIndex >= 0 && zIndex < self.pages.length) {
        const snapshot = getSnapshot(self.pages[idx]);
        self.pages.splice(idx, 1);
        self.pages.splice(zIndex, 0, snapshot);
      }
    },
    deletePages(ids) {
      const toRemove = self.pages.filter((p) => ids.includes(p.id));
      toRemove.forEach((page) => {
        destroy(page);
      });
      // ensure at least one page exists
      if (self.pages.length === 0) {
        self.addPage();
      }
      // fix active page if deleted
      if (!self.pages.find((p) => p.id === self._activePageId)) {
        self._activePageId = self.pages[0].id;
      }
    },
    groupElements(ids, attrs = {}) {
      const page = self.activePage;
      if (!page) return null;

      const elements = ids
        .map((id) => {
          const idx = page.children.findIndex((c) => c.id === id);
          if (idx >= 0) return { idx, snapshot: getSnapshot(page.children[idx]) };
          return null;
        })
        .filter(Boolean);

      if (elements.length < 2) return null;

      // Calculate group bounds
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      elements.forEach(({ snapshot }) => {
        minX = Math.min(minX, snapshot.x || 0);
        minY = Math.min(minY, snapshot.y || 0);
        maxX = Math.max(maxX, (snapshot.x || 0) + (snapshot.width || 0));
        maxY = Math.max(maxY, (snapshot.y || 0) + (snapshot.height || 0));
      });

      // Remove original elements (in reverse order to preserve indices)
      const sortedIndices = elements.map((e) => e.idx).sort((a, b) => b - a);
      sortedIndices.forEach((idx) => {
        page.children.splice(idx, 1);
      });

      // Adjust children positions relative to group
      const groupChildren = elements.map(({ snapshot }) => ({
        ...snapshot,
        id: undefined, // will get new id
        x: (snapshot.x || 0) - minX,
        y: (snapshot.y || 0) - minY,
      }));

      const group = page.addElement({
        type: 'group',
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        children: groupChildren,
        ...attrs,
      });

      return group;
    },
    ungroupElements(ids) {
      const page = self.activePage;
      if (!page) return;

      const newChildIds = [];

      ids.forEach((id) => {
        const idx = page.children.findIndex((c) => c.id === id);
        if (idx < 0) return;
        const group = page.children[idx];
        if (group.type !== 'group') return;

        const groupSnapshot = getSnapshot(group);
        const groupX = groupSnapshot.x || 0;
        const groupY = groupSnapshot.y || 0;

        // Remove group
        page.children.splice(idx, 1);

        // Re-add children at group's position
        (groupSnapshot.children || []).forEach((childSnapshot) => {
          const newEl = page.addElement(
            {
              ...childSnapshot,
              id: undefined,
              x: (childSnapshot.x || 0) + groupX,
              y: (childSnapshot.y || 0) + groupY,
            },
            { skipSelect: true }
          );
          if (newEl) newChildIds.push(newEl.id);
        });
      });

      // Select the ungrouped children
      if (newChildIds.length > 0) {
        self.selectElements(newChildIds);
      }
    },
    deleteElements(ids) {
      self.pages.forEach((page) => {
        const toRemove = page.children.filter((c) => ids.includes(c.id));
        toRemove.forEach((el) => {
          destroy(el);
        });
      });
      // Remove from selection
      self.selectedElementsIds.replace(
        self.selectedElementsIds.filter((id) => !ids.includes(id))
      );
    },
    on(event, callback) {
      if (!self._listeners.has(event)) {
        self._listeners.set(event, []);
      }
      self._listeners.get(event).push(callback);

      // Return disposer
      return () => {
        const listeners = self._listeners.get(event);
        if (listeners) {
          const idx = listeners.indexOf(callback);
          if (idx >= 0) listeners.splice(idx, 1);
        }
      };
    },
    _emit(event, data) {
      const listeners = self._listeners.get(event);
      if (listeners) {
        listeners.forEach((cb) => cb(data));
      }
    },
    _setStageRef(ref) {
      self._stageRef = ref;
    },

    async _toCanvas({
      pixelRatio = 1,
      ignoreBackground = false,
      pageId,
      mimeType = 'image/png',
      includeBleed = false,
      _skipTimeout = false,
      quickMode = false,
    } = {}) {
      const stage = self._stageRef;
      if (!stage) {
        throw new Error('Stage ref not set. Make sure <Workspace> is mounted.');
      }

      const page = pageId
        ? self.pages.find((p) => p.id === pageId)
        : self.activePage;
      if (!page) {
        throw new Error('No page found for export.');
      }

      // The workspace renders content in a Layer with offset/scale for centering.
      // For export we temporarily reset the layer transform to capture just the
      // design at its native resolution.
      const layer = stage.getLayers()[0];
      if (!layer) {
        throw new Error('No layer found in stage for export.');
      }

      const prevX = layer.x();
      const prevY = layer.y();
      const prevScaleX = layer.scaleX();
      const prevScaleY = layer.scaleY();

      layer.x(0);
      layer.y(0);
      layer.scaleX(1);
      layer.scaleY(1);

      // Hide display-only nodes (shadow, checkerboard, overlay) — not wanted in export
      const displayOnlyNodes = layer.find('.display-only');
      displayOnlyNodes.forEach((n) => n.hide());

      // Hide selection transformer handles so they don't appear in exports
      const transformerNodes = layer.find('Transformer');
      transformerNodes.forEach((n) => n.hide());

      layer.draw();

      const rawCanvas = layer.toCanvas({
        pixelRatio,
        x: 0,
        y: 0,
        width: self.width,
        height: self.height,
      });

      // Restore display-only nodes and transformer handles
      displayOnlyNodes.forEach((n) => n.show());
      transformerNodes.forEach((n) => n.show());

      // Restore layer transform
      layer.x(prevX);
      layer.y(prevY);
      layer.scaleX(prevScaleX);
      layer.scaleY(prevScaleY);
      layer.draw();

      // For JPEG, composite onto a white canvas to eliminate any remaining transparency
      // (avoids black areas and reduces file size vs checkerboard patterns)
      if (mimeType === 'image/jpeg') {
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = rawCanvas.width;
        finalCanvas.height = rawCanvas.height;
        const ctx = finalCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
        ctx.drawImage(rawCanvas, 0, 0);
        return finalCanvas;
      }

      return rawCanvas;
    },

    async toDataURL({
      mimeType = 'image/png',
      quality = 1,
      dpi,
      dpiMetadata,
      ...rest
    } = {}) {
      const canvas = await self._toCanvas({ mimeType, ...rest });
      return canvas.toDataURL(mimeType, quality);
    },

    async toBlob({ mimeType = 'image/png', quality = 0.92, ...rest } = {}) {
      const canvas = await self._toCanvas({ mimeType, ...rest });
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          mimeType,
          quality
        );
      });
    },

    async saveAsImage({ fileName = 'design.png', ...exportProps } = {}) {
      const dataURL = await self.toDataURL(exportProps);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataURL;
      link.click();
    },

    async toPDFDataURL({
      pixelRatio = 2,
      pageId,
      dpi = 300,
      includeBleed = false,
    } = {}) {
      const pages = pageId
        ? [self.pages.find((p) => p.id === pageId)].filter(Boolean)
        : [...self.pages];

      if (pages.length === 0) {
        throw new Error('No pages found for PDF export.');
      }

      const widthPt = (self.width / 96) * 72;
      const heightPt = (self.height / 96) * 72;

      const orientation = widthPt > heightPt ? 'landscape' : 'portrait';
      const pdf = new jsPDF({
        orientation,
        unit: 'pt',
        format: [widthPt, heightPt],
        compress: true,
      });

      const prevActivePageId = self._activePageId;

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) {
          pdf.addPage([widthPt, heightPt], orientation);
        }

        self._activePageId = pages[i].id;

        await new Promise((r) => setTimeout(r, 50));

        const canvas = await self._toCanvas({
          pixelRatio,
          pageId: pages[i].id,
          mimeType: 'image/jpeg',
          includeBleed,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, widthPt, heightPt, undefined, 'FAST');
      }

      self._activePageId = prevActivePageId;

      return pdf.output('datauristring');
    },

    async saveAsPDF({ fileName = 'design.pdf', ...exportProps } = {}) {
      const dataURL = await self.toPDFDataURL(exportProps);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataURL;
      link.click();
    },

    saveAsJSON({ fileName = 'design.json', stripBase64 = false } = {}) {
      const data = stripBase64 ? stripBase64FromExport(self.toJSON()) : self.toJSON();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    },

    async waitLoading({ _skipTimeout = false } = {}) {
      // Wait for all images to load
      return new Promise((resolve) => {
        setTimeout(resolve, _skipTimeout ? 0 : 100);
      });
    },

    toJSON() {
      return {
        width: self.width,
        height: self.height,
        fonts: self.fonts.map((f) => getSnapshot(f)),
        pages: self.pages.map((p) => p.toJSON()),
        audios: self.audios.map((a) => a.toJSON()),
        unit: self.unit,
        dpi: self.dpi,
        custom: self.custom,
        schemaVersion: self.schemaVersion,
      };
    },

    loadJSON(json, keepHistory = false) {
      if (!json) return;

      const data = typeof json === 'string' ? JSON.parse(json) : json;

      self.history.ignore(() => {
        if (data.width !== undefined) self.width = data.width;
        if (data.height !== undefined) self.height = data.height;
        if (data.unit !== undefined) self.unit = data.unit;
        if (data.dpi !== undefined) self.dpi = data.dpi;
        if (data.custom !== undefined) self.custom = data.custom;
        if (data.schemaVersion !== undefined) self.schemaVersion = data.schemaVersion;

        // Load fonts
        if (data.fonts) {
          self.fonts.clear();
          data.fonts.forEach((font) => {
            self.fonts.push(font);
          });
        }

        // Load audios
        if (data.audios) {
          self.audios.clear();
          data.audios.forEach((audio) => {
            self.audios.push(audio);
          });
        }

        // Load pages
        if (data.pages) {
          self.pages.clear();
          data.pages.forEach((pageData) => {
            self.pages.push(pageData);
          });
        }

        // Set active page
        if (self.pages.length > 0) {
          self._activePageId = self.pages[0].id;
        }

        // Clear selection
        self.selectedElementsIds.clear();
      });

      // Reset scale before saving the initial undo state so that history[0]
      // always records scale=1 (the intended post-load baseline). Without this,
      // history[0] would capture whatever scale was active before loadJSON ran
      // (e.g. the auto-fit of a previous template or a zero-width-container
      // negative value), and the first undo would restore that wrong scale —
      // which, when negative, mirrors the entire canvas.
      self.scale = 1;

      if (!keepHistory) {
        self.history.clear();
        self.history.addUndoState();
      }

      // Auto-load fonts referenced in text elements
      self._autoLoadFonts(data);

      // Auto-populate pending input fields so the dialog triggers
      self.requestInputFields();
    },

    clear({ keepHistory = false } = {}) {
      self.pages.clear();
      self.fonts.clear();
      self.audios.clear();
      self.selectedElementsIds.clear();
      self._activePageId = '';
      self.width = 500;
      self.height = 500;
      self.custom = {};

      self.addPage();

      if (!keepHistory) {
        self.history.clear();
        self.history.addUndoState();
      }
    },

    addFont(font) {
      const existing = self.fonts.find((f) => f.fontFamily === font.fontFamily);
      if (!existing) {
        self.fonts.push(font);
      }
    },

    removeFont(fontFamily) {
      const idx = self.fonts.findIndex((f) => f.fontFamily === fontFamily);
      if (idx >= 0) {
        self.fonts.splice(idx, 1);
      }
    },

    addAudio(data) {
      self.audios.push({ id: uuidv4(), ...data });
    },

    removeAudio(audioId) {
      const idx = self.audios.findIndex((a) => a.id === audioId);
      if (idx >= 0) {
        self.audios.splice(idx, 1);
      }
    },

    async loadFont(fontFamily, text = '') {
      // Load font via Google Fonts CSS API
      const id = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;

      try {
        if (!document.getElementById(id)) {
          const link = document.createElement('link');
          link.id = id;
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
            fontFamily
          )}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
          link.rel = 'stylesheet';

          // Wait for the CSS stylesheet to load so @font-face rules are parsed
          await new Promise((resolve) => {
            link.onload = resolve;
            link.onerror = resolve; // don't block on network errors
            document.head.appendChild(link);
          });
        }

        // Now the browser knows about the @font-face, load the actual font files
        await document.fonts.load(`16px "${fontFamily}"`);
        await document.fonts.load(`bold 16px "${fontFamily}"`);
        await document.fonts.ready;

        // Bump version so MobX observers re-render with the real font
        self._fontVersion += 1;
        // Force Konva to redraw all text nodes with the newly loaded font
        if (self._stageRef) {
          self._stageRef.batchDraw();
        }
      } catch (e) {
        console.warn(`Failed to load font: ${fontFamily}`, e);
      }
    },

    _autoLoadFonts(data) {
      // Collect all unique fontFamily values from text elements across all pages
      const fonts = new Set();
      const collectFromChildren = (children) => {
        if (!Array.isArray(children)) return;
        children.forEach((el) => {
          if (el.type === 'text' && el.fontFamily) {
            fonts.add(el.fontFamily);
          }
          if (el.children) {
            collectFromChildren(el.children);
          }
        });
      };

      if (data.pages) {
        data.pages.forEach((page) => {
          collectFromChildren(page.children);
        });
      }

      // Also load fonts from the fonts array in the JSON
      if (data.fonts) {
        data.fonts.forEach((f) => {
          if (f.fontFamily) fonts.add(f.fontFamily);
        });
      }

      // Load each unique font
      fonts.forEach((fontFamily) => {
        self.loadFont(fontFamily);
      });
    },

    validate(json) {
      const errors = [];
      if (!json) {
        errors.push({ path: '', message: 'JSON is empty' });
        return errors;
      }
      if (typeof json.width !== 'number') {
        errors.push({ path: 'width', message: 'width must be a number' });
      }
      if (typeof json.height !== 'number') {
        errors.push({ path: 'height', message: 'height must be a number' });
      }
      if (!Array.isArray(json.pages)) {
        errors.push({ path: 'pages', message: 'pages must be an array' });
      }
      return errors;
    },

    checkActivePage() {
      if (self.pages.length > 0 && !self.activePage) {
        self._activePageId = self.pages[0].id;
      }
    },

    setCurrentTime(time) {
      self.currentTime = time;
    },

    _togglePlaying(playing = true) {
      self.isPlaying = playing;
    },

    play() {
      self._togglePlaying(true);
    },

    stop() {
      self._togglePlaying(false);
    },

    seek() {
      // placeholder
    },
    _initConfig(key, showCredit) {
      self._key = key;
      self._showCredit = showCredit;
    },
  }));

export function createStore({ key = '', showCredit = false } = {}) {
  const store = Store.create({});
  store._initConfig(key, showCredit);
  return store;
}

export default createStore;
